import { useState } from 'react';
import EToolTip from 'components/others/EToolTip';

export interface UserAvatarItem {
    id: number | string;
    name?: string | null;
    picture?: string | null;
    hint?: string | null;
}

export interface UserAvatarStackProps {
    items: UserAvatarItem[];
    maxVisible?: number;
    showNameForOne?: boolean;
}

const FALLBACK = '/imgs/user.png';

const AvatarImage = ({
    item,
    withTooltip,
}: {
    item: UserAvatarItem;
    withTooltip: boolean;
}) => {
    const label = item.hint || item.name || '';
    const image = (
        <img
            className="user-avatar-stack__img"
            src={item.picture || FALLBACK}
            alt={item.name || ''}
            onError={(event) => {
                event.currentTarget.src = FALLBACK;
            }}
        />
    );
    if (!withTooltip || !label) return image;
    return <EToolTip title={label}>{image}</EToolTip>;
};

const UserAvatarStack = ({
    items,
    maxVisible = 4,
    showNameForOne = false,
}: UserAvatarStackProps) => {
    const [expanded, setExpanded] = useState(false);
    if (!items.length) return <span className="text-muted">—</span>;

    const showSingleName = showNameForOne && items.length === 1 && !expanded;
    const canExpand = items.length > 1;
    const visible = items.length > maxVisible ? items.slice(0, maxVisible) : items;
    const surplus = items.length > maxVisible ? items.length - maxVisible : 0;

    if (showSingleName || expanded) {
        const rows = expanded ? items : items.slice(0, 1);
        const body = (
            <div className="user-avatar-stack user-avatar-stack--names">
                {rows.map((item) => (
                    <div key={item.id} className="user-avatar-stack__named">
                        <AvatarImage item={item} withTooltip={false} />
                        <span className="text-truncate">{item.name || '—'}</span>
                    </div>
                ))}
            </div>
        );
        if (!canExpand) return body;
        return (
            <button
                type="button"
                className="user-avatar-stack__toggle"
                onClick={() => setExpanded((value) => !value)}
            >
                {body}
            </button>
        );
    }

    return (
        <div className="user-avatar-stack">
            {visible.map((item) => (
                <AvatarImage key={item.id} item={item} withTooltip />
            ))}
            {surplus > 0 ? (
                <button
                    type="button"
                    className="user-avatar-stack__more"
                    onClick={() => setExpanded(true)}
                >
                    +{surplus}
                </button>
            ) : null}
        </div>
    );
};

export default UserAvatarStack;
